import { DateInput, DateInputProps } from '@mantine/dates';
import { IconCalendarEvent, IconInfoCircle } from '@tabler/icons-react';
import {
  EN_LOCALE_DATE_FORMAT,
  parseDateUnknown,
} from '@zorro/shared/formatters';
import { useForwardRef } from '@zorro/web/ui-utils';
import { KeyboardEvent, ReactNode, forwardRef } from 'react';

import { ZorroIcon } from '../../Icon';
import { brand } from '../../theme';
import { ReadOnlyLock } from '../../v2/ReadOnlyLock';
import classNames from './DateInput.module.scss';
import {
  calendarDropdownStyles,
  calendarPanelProps,
  dateInputClassNames,
} from './calendarPanel';

type IDateInputProps = Omit<DateInputProps, 'onChange' | 'readOnly'> & {
  isMasked?: boolean;
  isReadOnly?: boolean;
  iconColor?: string;
  readOnlyVariant?: 'label' | 'lock';
  withCalendarIcon?: boolean;
  onChange?: (value: Date | null) => void;
};

/** Wrap a description/info message with a leading info-circle icon, mirroring
 * the V2 TextInput info state. String descriptions get the icon; non-string
 * ReactNodes pass through untouched; empty → undefined. */
const renderDescription = (
  description: DateInputProps['description']
): ReactNode => {
  if (!description) return undefined;
  if (typeof description !== 'string') return description;

  return (
    <span className={classNames.descriptionWrapper}>
      <IconInfoCircle size={12} stroke={2} />
      <span>{description}</span>
    </span>
  );
};

const mergeStylesByKey = <T extends Record<string, unknown> | undefined>(
  internal: T,
  external: T
): T => {
  if (!internal) return external;
  if (!external) return internal;
  const merged: Record<string, unknown> = { ...internal };
  for (const key of Object.keys(external)) {
    const internalVal = (internal as Record<string, unknown>)[key];
    const externalVal = (external as Record<string, unknown>)[key];
    merged[key] =
      internalVal && externalVal
        ? { ...(internalVal as object), ...(externalVal as object) }
        : (externalVal ?? internalVal);
  }
  return merged as T;
};

export const ZorroDateInput = forwardRef<HTMLInputElement, IDateInputProps>(
  (
    {
      rightSection,
      onChange,
      placeholder = EN_LOCALE_DATE_FORMAT,
      size = 'md',
      clearable = false,
      isMasked = false,
      iconColor = brand.zorroGray400,
      isReadOnly,
      readOnlyVariant = 'label',
      withCalendarIcon = true,
      description,
      styles: externalStyles,
      popoverProps: externalPopoverProps,
      ...props
    },
    ref
  ) => {
    const innerRef = useForwardRef<HTMLInputElement | null>(ref);

    const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        innerRef.current?.blur();
      }
    };

    const inputClassNames = [
      classNames.input ?? null,
      isMasked ? 'fs-mask' : null,
    ].filter((className): className is string => Boolean(className));

    const mergedPopoverProps = {
      ...externalPopoverProps,
      styles: mergeStylesByKey(
        calendarDropdownStyles as Record<string, unknown>,
        externalPopoverProps?.styles as Record<string, unknown> | undefined
      ),
    };

    const isReadOnlyLock = Boolean(isReadOnly) && readOnlyVariant === 'lock';

    let defaultRightSection: ReactNode;
    if (isReadOnlyLock) {
      defaultRightSection = (
        <ReadOnlyLock size={20} stroke={1.5} color={iconColor} />
      );
    } else if (withCalendarIcon) {
      defaultRightSection = (
        <ZorroIcon icon={IconCalendarEvent} color={iconColor} size={20} />
      );
    }

    return (
      <DateInput
        ref={innerRef}
        placeholder={placeholder}
        size={size}
        clearable={clearable}
        classNames={{
          ...dateInputClassNames,
          input: inputClassNames.join(' '),
        }}
        readOnly={isReadOnly}
        data-readonly-variant={isReadOnlyLock ? 'lock' : undefined}
        description={renderDescription(description)}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
        rightSection={rightSection ?? defaultRightSection}
        onKeyDown={handleOnKeyDown}
        onChange={(val) => {
          onChange?.(val ? parseDateUnknown(val).toDate() : null);
        }}
        {...calendarPanelProps}
        {...props}
        styles={externalStyles}
        popoverProps={mergedPopoverProps}
      />
    );
  }
);

ZorroDateInput.displayName = 'ZorroDateInput';
